/** Units are canvas pixels internally, converted to world units at the root */
export interface UIStyle {
  // Layout
  direction?: 'row' | 'column';
  alignItems?: 'start' | 'center' | 'end';
  justifyContent?: 'start' | 'center' | 'end' | 'space-between';
  gap?: number;
  padding?: number | [number, number] | [number, number, number, number];
  margin?: number | [number, number] | [number, number, number, number];

  // Sizing (canvas pixels)
  width?: number | 'auto';
  height?: number | 'auto';
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;

  // Text overflow
  overflow?: 'visible' | 'ellipsis';

  // Background
  backgroundColor?: string;
  borderRadius?: number;
  borderColor?: string;
  borderWidth?: number;

  // Text (only for text nodes)
  fontSize?: number;
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold';
  color?: string;
  textAlign?: 'left' | 'center' | 'right';

  // Effects
  opacity?: number;
}

export interface UICircleConfig {
  radius: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  text?: string;
  textSize?: number;
  image?: HTMLImageElement;
}

export interface UITriangleConfig {
  width: number;
  height: number;
  direction?: 'left' | 'right' | 'up' | 'down';
  fill?: string;
}

export interface UINodeConfig {
  tag?: string;
  style?: UIStyle;
  text?: string;
  children?: UINodeConfig[];
  circle?: UICircleConfig;
  triangle?: UITriangleConfig;
  /** Draw an image filling this node's layout rect (no clipping) */
  image?: HTMLImageElement;
}

export interface UIComponentConfig {
  /** World-space width. If omitted, derived from worldHeight + canvas aspect ratio. */
  worldWidth?: number;
  /** World-space height. If omitted, derived from worldWidth + canvas aspect ratio. */
  worldHeight?: number;
  mode?: 'sprite' | 'mesh';
  resolution?: number;
  renderOrder?: number;
  depthTest?: boolean;
  depthWrite?: boolean;
  spriteCenter?: { x: number; y: number };
}

export interface LayoutRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type Padding = [top: number, right: number, bottom: number, left: number];
export type Margin = [top: number, right: number, bottom: number, left: number];

// Helper: normalize padding input to [t, r, b, l]
export function resolvePadding(p: UIStyle['padding']): Padding {
  if (p == null) return [0, 0, 0, 0];
  if (typeof p === 'number') return [p, p, p, p];
  if (p.length === 2) return [p[0], p[1], p[0], p[1]];
  return [p[0], p[1], p[2], p[3]];
}

// Helper: normalize margin input to [t, r, b, l]
export function resolveMargin(m: UIStyle['margin']): Margin {
  if (m == null) return [0, 0, 0, 0];
  if (typeof m === 'number') return [m, m, m, m];
  if (m.length === 2) return [m[0], m[1], m[0], m[1]];
  return [m[0], m[1], m[2], m[3]];
}

// Helper: build CSS font string from style
export function buildFont(style: UIStyle): string {
  const weight = style.fontWeight ?? 'normal';
  const size = style.fontSize ?? 16;
  const family = style.fontFamily ?? 'Rajdhani, sans-serif';
  return `${weight} ${size}px ${family}`;
}

// Defaults
export const DEFAULT_STYLE: Required<
  Pick<
    UIStyle,
    | 'direction'
    | 'alignItems'
    | 'justifyContent'
    | 'gap'
    | 'fontSize'
    | 'fontFamily'
    | 'fontWeight'
    | 'color'
    | 'textAlign'
    | 'borderRadius'
    | 'borderWidth'
    | 'opacity'
  >
> = {
  direction: 'column',
  alignItems: 'start',
  justifyContent: 'start',
  gap: 0,
  fontSize: 16,
  fontFamily: 'Rajdhani, sans-serif',
  fontWeight: 'normal',
  color: '#FFFFFF',
  textAlign: 'left',
  borderRadius: 0,
  borderWidth: 0,
  opacity: 1,
};
